import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Drawer, Avatar, Tooltip } from 'antd';
import { X, Phone, Buildings, PhoneCall, ClockCounterClockwise, FileText, CaretDoubleLeft, CaretDoubleRight, Microphone, Folder, TestTube, IdentificationCard, Copy, ArrowSquareOut } from '@phosphor-icons/react';
import Typography from '../../../components/Typography';
import Button from '../../../components/Button';
import './outboundSlideOut.scss';
import COLORS from '../../../util/constants/colors';
import CallTranscript from '../CallTranscript';
import { hasTranscriptContent } from '../transcriptContent';
import VoiceRecording from '../VoiceRecording';
import CallQualityFeedback from '../CallQualityFeedback';
import axios from 'axios';
import moment from 'moment';
import { CALL_OUTCOME_TAG_STYLE, CALL_OUTCOME_TAGS } from './constants';
import { formatDOB, formatPhoneNumber, formatName, copyValue } from '../helpers';
import { captureAnalytics } from '../../../actions/preload';

// ENG-3107: the return trip for the inbound slide-out's "View in Voice Outbound"
// link. `inbound_delivery_call_id` is the inbound call's Firestore document id,
// which is exactly what the Voice Inbox `?callId=` deep link expects, and the
// server only sets it for orgs entitled to the ENG-3061 delivery outcomes.
//
// Voice keeps its active tab in the `type` query param, so this is a same-route
// link: it switches to the Inbound table and opens that call's slide-out.
//
// `result_delivery` is load-bearing, not cosmetic. VoiceInbound resolves
// `?callId=` against the rows ALREADY LOADED (`data.findIndex(...)`) and defaults
// to 10 per page with no date window, so without narrowing, any call older than
// the ten most recent silently switches tabs and opens nothing. Both states are
// sent deliberately: the target necessarily matches one of them (it is the same
// attempt `ResultDelivery.exists_sql` matches on), so the filter can never
// exclude it, and the client needn't duplicate the server's outcome => state map.
//
// Rejected: a date window off `started_at` (that is the OUTBOUND attempt's
// timestamp — nothing guarantees it shares a day with the inbound call, so a wrong
// window would filter the target out), and `search=<patient name>` (inbound search
// matches `caller_name`, who need not be the patient, and it would put a patient
// name in the URL).
//
// On the Firestore read path the server does not accept this filter and the inbound
// reconciliation drops it, so the link degrades to today's behaviour rather than
// breaking; the narrowing turns on with `voice_inbox_sql_read_path`.
const inboundCallHref = (inboundCallId) => {
  const params = new URLSearchParams({
    type: 'Inbound',
    callId: String(inboundCallId),
    result_delivery: 'delivered,unverified',
  });
  return `?${params.toString()}`;
};

const fetchOutboundCalls = (id) => axios({
  method: 'GET',
  url: `/api/v1/outbound_calls/${id}`,
  headers: {
    'X-CSRF-Token': document.getElementsByName('csrf-token')[0].content,
  },
})
  .then((response) => response.data.data.calls || [])
  .catch(() => []);

const OutboundSlideOut = ({
  open,
  onClose,
  record,
  setCallIndex,
  disableNext,
  disablePrevious,
  isNavigationLoading,
}) => {
  const width = window.innerWidth > 768 ? 900 : '100%';
  const [calls, setCalls] = useState([]);

  // The drawer stays MOUNTED while the user moves between outbound requests —
  // next/previous swap the `record` prop, and arriving here from the inbound
  // cross-link is client-side routing too — so this fetch re-runs on `record.id`
  // with the previous request's response possibly still in flight.
  //
  // Two rules, both of which the response must obey before it may be displayed:
  //
  //   * `active` discards a response for a request the user has already navigated
  //     away from. Without it a slow response resolves LAST and wins, and nothing
  //     refetches afterwards, so the wrong data persists indefinitely.
  //   * clearing up front means the in-flight window renders nothing rather than
  //     the previous request's calls.
  //
  // This matters beyond a cosmetic lag: the header and CallTranscript's `userName`
  // come from the `record` prop and are always fresh, while the transcript,
  // recording, SID and inbound cross-link all come from `calls`. A stale `calls`
  // therefore renders one patient's call content under another patient's name.
  useEffect(() => {
    setCalls([]);
    if (!record?.id) return undefined;

    if (Array.isArray(record.calls)) {
      setCalls(record.calls);
      return undefined;
    }

    let active = true;
    fetchOutboundCalls(record.id).then((nextCalls) => {
      if (active) setCalls(nextCalls);
    });

    return () => {
      active = false;
    };
  }, [record?.id, record?.calls]);

  // Cleanup: pause all audio elements when slideout closes or when navigating to a different call
  useEffect(() => {
    const audioElements = document.querySelectorAll('.outbound-slide-out audio');
    audioElements.forEach((audio) => {
      audio.pause();
    });
  }, [open, record?.id]);

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'SJ';
  };

  const InfoCard = ({ icon, label, value }) => {
    return (
      <div className='info-card'>
        <div className='info-card-title'>
          {icon}
          <Typography
            variant='text'
            size='small'
            className='info-card-label'
            color={COLORS.TEXT_SECONDARY_LITE}
          >
            {label}
          </Typography>
        </div>
        <div className='info-card-content'>
          <Typography variant='text' size='small' className='info-card-value'>
            {value}
          </Typography>
        </div>
      </div>
    );
  };

  const title = (
    <>
      <div className='outbound-slide-out-header'>
        <div className='outbound-slide-out-header-title'>
          <Avatar size={56} className='patient-avatar'>
            {getInitials(record?.patient_name)}
          </Avatar>
          <div className='patient-info'>
            <Typography variant='text' size='large' bold className='patient-name'>
              {formatName(record?.patient_name)}
            </Typography>
            <Typography variant='text' size='small' className='patient-dob'>
              {/** TODO: add here the DOB format - formatBirthdate(voiceCall?.caller_dob) */}
              DOB: {formatDOB(record?.patient_dob)}
            </Typography>
          </div>
        </div>
        <div className='outbound-slide-out-header-buttons'>
          <Tooltip title='Previous Call' placement='bottom'>
            <Button
              className='icon-btn tertiary medium'
              icon={<CaretDoubleLeft weight='bold' size={20} />}
              onClick={() => setCallIndex('previous')}
              disabled={disablePrevious || isNavigationLoading}
            />
          </Tooltip>
          <Tooltip title='Next Call' placement='bottom'>
            <Button
              className='icon-btn tertiary medium'
              icon={<CaretDoubleRight weight='bold' size={20} />}
              onClick={() => setCallIndex('next')}
              disabled={disableNext || isNavigationLoading}
            />
          </Tooltip>
          <Tooltip title='Close' placement='bottom'>
            <Button
              className='icon-btn tertiary medium'
              icon={<X weight='bold' size={20} />}
              onClick={onClose}
            />
          </Tooltip>
        </div>
      </div>
      <div className='info-cards'>
        <InfoCard icon={<Phone size={16} />} label='Phone' value={formatPhoneNumber(record?.phone_number)} />
        <InfoCard icon={<Buildings size={16} />} label='Workflow' value={record?.workflow_name} />
        <InfoCard icon={<PhoneCall size={16} />} label='Total Calls' value={record?.total_calls} />
        <InfoCard icon={<TestTube size={16} />} label='Specimen IDs' value={record?.specimen_ids.join(', ')} />
        {record?.mrn && <InfoCard icon={<IdentificationCard size={16} />} label='MRN' value={record?.mrn} />}
      </div>
    </>
  );

  // Gated outcomes are redacted to null by the server for orgs without the
  // feature, so this only tags known values and falls back to "No outcome".
  const CallOutcomeTag = ({ outcome }) => {
    if (CALL_OUTCOME_TAGS[outcome]) {
      return (
        <div style={{
          ...CALL_OUTCOME_TAG_STYLE,
          color: CALL_OUTCOME_TAGS[outcome].color,
          border: `1px solid ${CALL_OUTCOME_TAGS[outcome].color}`,
          backgroundColor: CALL_OUTCOME_TAGS[outcome].backgroundColor,
          maxWidth: 'unset',
        }}>
          {CALL_OUTCOME_TAGS[outcome].icon}
          {CALL_OUTCOME_TAGS[outcome].label}
        </div>
      );
    }
    return (
      <Typography variant='text' size='small'>
        {outcome || 'No outcome'}
      </Typography>
    );
  };

  return (
    <Drawer
      visible={open}
      onClose={onClose}
      width={width}
      closable={false}
      className='outbound-slide-out-drawer'
      title={title}
    >
      <div className='outbound-slide-out'>
        <div className='call-history-section'>
          <div className='section-header'>
            <ClockCounterClockwise size={20} weight='fill' />
            <Typography variant='text' size='medium' bold color={COLORS.TEXT_SECONDARY_LITE}>
              Call History ({calls.length} calls)
            </Typography>
          </div>

            {calls.map((call) => {
              // hasTranscriptContent is the single definition of "CallTranscript
              // would render something here" -- it normalizes every empty shape the
              // value arrives in (null, the '[]' sentinel, a real empty array) AND
              // applies the same tool-call/empty-content filter the renderer does.
              // Deferring to it is load-bearing: a transcript can be non-empty and
              // still paint nothing, so any check derived independently here would
              // eventually disagree with what is actually on screen.
              const hasRealCallContent = Boolean(call.call_recording) || hasTranscriptContent(call.transcript);
              // Recording/feedback/transcript are guaranteed blank ONLY for an
              // inbound-delivered attempt that also has no real content of its own --
              // an ordinary attempt (no inbound delivery) always keeps them, and an
              // inbound-delivered attempt that DOES have real content (this leg also
              // held a conversation) keeps them too, alongside the cross-link.
              const hideEmptySectionsForInboundDelivery = Boolean(call.inbound_delivery_call_id)
                && !hasRealCallContent;

              return (
              <div className='call-history-item' key={call.id}>
                <div className='call-header'>
                  <div className='call-number'>{call.attempt_number}</div>
                  <div className='call-header-content'>
                    <div className='call-date-type'>
                      <Typography variant='text' size='small' className='call-date'>
                        {moment(call.started_at).format('MM/DD/YYYY hh:mm A')}
                      </Typography>
                      <Typography variant='text' size='xsmall' className='call-type'>
                        {call.type}
                      </Typography>
                    </div>
                  </div>
                  <CallOutcomeTag outcome={call.outcome} />
                  <Typography variant='text' size='small' color={COLORS.TEXT_SECONDARY_LITE}>
                    {call.formatted_duration}
                  </Typography>
                </div>

                {/* Call Summary */}
                <div className='call-details'>
                  <div className='call-summary-section'>
                    <div className='subsection-header'>
                      <FileText size={24} weight='fill' />
                      <Typography variant='text' size='small' className='subsection-title'>
                        Call Summary
                      </Typography>
                    </div>
                    <Typography variant='text' size='xsmall' className='summary-text'>
                      {call.summary || 'No summary available'}
                    </Typography>
                  </div>

                  {call.inbound_delivery_call_id && (
                    // The result reached the patient because they called the inbound
                    // line instead of this leg holding the conversation. Surface where
                    // the delivery actually happened, prominently, alongside whatever
                    // real content (recording/transcript) this leg does have.
                    <a
                      className='outbound-inbound-delivery-link outbound-inbound-delivery-link-prominent'
                      href={inboundCallHref(call.inbound_delivery_call_id)}
                      data-testid='inbound-delivery-link'
                      onClick={() => captureAnalytics('voice_outbound_inbound_delivery_link_clicked', {
                        source: 'outbound_slide_out',
                      })}
                    >
                      View the inbound call that delivered this result
                      <ArrowSquareOut size={18} weight='bold' aria-hidden='true' />
                    </a>
                  )}

                  {!hideEmptySectionsForInboundDelivery && (
                    <>
                      <div className='outbound-call-recording'>
                        <div className='subsection-header'>
                          <Microphone size={24} weight='fill' />
                          <Typography variant='text' size='small' className='subsection-title'>
                            Call Recording
                          </Typography>
                        </div>
                        <div className='recording-and-feedback'>
                          <VoiceRecording
                            callId={call.call_recording ? call.call_sid : null}
                            isOutbound={true}
                          />
                          <CallQualityFeedback
                            callSid={call.call_sid}
                            isOutbound
                          />
                        </div>
                      </div>

                      <CallTranscript
                        transcript={call?.transcript}
                        userName={record?.patient_name}
                      />
                    </>
                  )}

                  {!hideEmptySectionsForInboundDelivery && call.call_sid && (
                    <div className='call-metadata-section'>
                      <Typography style={{ fontStyle: 'italic' }} variant='text' size='xsmall' color={COLORS.TEXT_SECONDARY_LITE}>
                        SID: {call.call_sid}
                      </Typography>
                      <Tooltip title='Copy SID' placement='bottom'>
                        <Button
                          className='icon-btn tertiary xsmall'
                          icon={<Copy weight='bold' size={20} />}
                          onClick={async () => await copyValue(call.call_sid, 'Call SID copied to clipboard', 'Failed to copy Call SID')}
                        />
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
        </div>
      </div>
    </Drawer>
  );
};

OutboundSlideOut.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  record: PropTypes.object,
  setCallIndex: PropTypes.func.isRequired,
  disableNext: PropTypes.bool,
  disablePrevious: PropTypes.bool,
  isNavigationLoading: PropTypes.bool,
};

OutboundSlideOut.defaultProps = {
  record: null,
  disableNext: false,
  disablePrevious: false,
  isNavigationLoading: false,
};

export default OutboundSlideOut;
